import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Bell,
  Check,
  CheckCircle2,
  History,
  Loader2,
  PauseCircle,
  Pencil,
  Pill,
  PlayCircle,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import TerapiaExtraDialog from "./TerapiaExtraDialog";
import TerapiaExtraStoricoDialog from "./TerapiaExtraStoricoDialog";
import { sortTerapieByOrarioNome, summarizeTerapiaExtra } from "../../utils/terapieExtraFormat";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AlertSlotRow({ slot, onSomministra, submitting, note, onNoteChange, readOnly, showOspiteName }) {
  const isOverdue = slot.stato === "overdue";
  const isDue = slot.stato === "due";
  const isSomministrata = !!slot.somministrata;
  const rowClass = isSomministrata
    ? "bg-emerald-50/70"
    : isOverdue
      ? "bg-red-50/80"
      : isDue
        ? "bg-amber-50/80"
        : "bg-white";

  const badgeClass = isSomministrata
    ? "bg-emerald-600"
    : isOverdue
      ? "bg-red-600"
      : isDue
        ? "bg-amber-500 text-white"
        : "bg-slate-500";

  const badgeLabel = isSomministrata
    ? "Somministrata"
    : isOverdue
      ? "In ritardo"
      : isDue
        ? "Da somministrare"
        : "Programmata";

  return (
    <tr className={`${rowClass} align-top transition-colors`}>
      <td className="whitespace-nowrap px-3 py-3 text-base font-bold tabular-nums text-slate-900">
        {slot.orario || "—"}
      </td>
      <td className="min-w-[220px] px-3 py-3">
        <p className="font-semibold text-slate-900">{slot.nome_terapia}</p>
        {showOspiteName && slot.nome_ospite ? (
          <p className="text-xs font-medium text-slate-500 mt-0.5">{slot.nome_ospite}</p>
        ) : null}
      </td>
      <td className="min-w-[220px] px-3 py-3">
        {slot.dosaggio ? (
          <p className="text-sm text-slate-700">{slot.dosaggio}</p>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        )}
        {slot.note_terapia ? (
          <p className="text-xs text-slate-500 mt-1 italic">{slot.note_terapia}</p>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-3">
        <Badge className={badgeClass}>{badgeLabel}</Badge>
        {isSomministrata ? (
          <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {slot.somministrata_da_nome || "Registrata"}
          </p>
        ) : null}
      </td>
      <td className="min-w-[240px] px-3 py-3">
        {!readOnly && !isSomministrata ? (
          <div className="flex flex-col gap-2">
            <Textarea
              placeholder="Note (opzionale)"
              value={note || ""}
              onChange={(e) => onNoteChange(slot, e.target.value)}
              rows={2}
              className="text-sm bg-white"
            />
            <Button
              size="sm"
              onClick={() => onSomministra(slot)}
              disabled={submitting}
              className={isOverdue ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Segna somministrata
            </Button>
          </div>
        ) : isSomministrata ? (
          <span className="text-sm text-emerald-700">Completata</span>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
}

export default function TerapieExtraSection({
  fascicoloId,
  terapieExtra = [],
  mode = "admin",
  authHeaders,
  onRefresh,
  onAlertsChange,
  showOspiteName = false,
}) {
  const isAdmin = mode === "admin";
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTerapia, setEditingTerapia] = useState(null);
  const [storicoTerapia, setStoricoTerapia] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);
  const [noteBySlot, setNoteBySlot] = useState({});

  const alertsUrl = isAdmin
    ? `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-extra/alerts?scope=today`
    : `${API_URL}/api/oss/me/fascicoli/${fascicoloId}/terapie-extra/alerts?scope=today`;

  const loadAlerts = useCallback(() => {
    if (!fascicoloId || !authHeaders) return;
    setLoadingAlerts(true);
    axios
      .get(alertsUrl, { headers: authHeaders })
      .then(({ data }) => setAlerts(data.items || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoadingAlerts(false));
  }, [alertsUrl, authHeaders, fascicoloId]);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  useEffect(() => {
    // Evita di azzerare i badge del tab prima del primo caricamento
    if (loadingAlerts) return;
    onAlertsChange?.(
      alerts.filter((slot) => !slot.somministrata && ["due", "overdue"].includes(slot.stato)).length
    );
  }, [alerts, loadingAlerts, onAlertsChange]);

  const slotKey = (slot) => `${slot.terapia_extra_id}::${slot.scheduled_at}`;

  const handleSomministra = async (slot) => {
    if (!fascicoloId || !authHeaders) return;
    const url = isAdmin
      ? `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-extra/${slot.terapia_extra_id}/somministra`
      : `${API_URL}/api/oss/me/fascicoli/${fascicoloId}/terapie-extra/${slot.terapia_extra_id}/somministra`;
    setSubmittingId(slotKey(slot));
    try {
      await axios.post(
        url,
        {
          scheduled_at: slot.scheduled_at,
          note: noteBySlot[slotKey(slot)] || null,
        },
        { headers: authHeaders }
      );
      toast.success("Somministrazione registrata");
      setNoteBySlot((prev) => {
        const next = { ...prev };
        delete next[slotKey(slot)];
        return next;
      });
      loadAlerts();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setSubmittingId(null);
    }
  };

  const handleCreateOrUpdate = async (payload) => {
    if (!isAdmin || !authHeaders) return;
    setSaving(true);
    try {
      if (editingTerapia?.id) {
        await axios.put(
          `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-extra/${editingTerapia.id}`,
          payload,
          { headers: authHeaders }
        );
        toast.success("Terapia extra aggiornata");
      } else {
        await axios.post(
          `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-extra`,
          payload,
          { headers: authHeaders }
        );
        toast.success("Terapia extra creata");
      }
      setDialogOpen(false);
      setEditingTerapia(null);
      onRefresh?.();
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio terapia");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAttiva = async (te) => {
    if (!isAdmin || !authHeaders) return;
    try {
      await axios.put(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-extra/${te.id}`,
        { attiva: !te.attiva },
        { headers: authHeaders }
      );
      toast.success(te.attiva ? "Terapia sospesa" : "Terapia riattivata");
      onRefresh?.();
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const handleDelete = async (te) => {
    if (!isAdmin || !authHeaders) return;
    if (!window.confirm(`Eliminare la terapia "${te.nome}"?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-extra/${te.id}`,
        { headers: authHeaders }
      );
      toast.success("Terapia extra rimossa");
      onRefresh?.();
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const sortedTerapie = sortTerapieByOrarioNome(terapieExtra);

  return (
    <>
      <Card className="card-base lg:col-span-2">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="w-5 h-5 text-rose-500" />
            Terapie Extra
            {alerts.filter((slot) => !slot.somministrata && ["due", "overdue"].includes(slot.stato)).length > 0 ? (
              <Badge className="bg-red-500 text-white">
                {alerts.filter((slot) => !slot.somministrata && ["due", "overdue"].includes(slot.stato)).length}
              </Badge>
            ) : null}
          </CardTitle>
          {isAdmin ? (
            <Button
              size="sm"
              onClick={() => {
                setEditingTerapia(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi terapia extra
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Somministrazioni di oggi
            </h4>
            {loadingAlerts && alerts.length === 0 ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : alerts.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna terapia extra prevista oggi.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-[860px] w-full border-collapse text-left">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Orario</th>
                      <th className="px-3 py-2 font-semibold">Terapia</th>
                      <th className="px-3 py-2 font-semibold">Dettagli</th>
                      <th className="px-3 py-2 font-semibold">Stato</th>
                      <th className="px-3 py-2 font-semibold">Azione</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {alerts.map((slot) => (
                      <AlertSlotRow
                        key={slotKey(slot)}
                        slot={slot}
                        onSomministra={handleSomministra}
                        submitting={submittingId === slotKey(slot)}
                        note={noteBySlot[slotKey(slot)]}
                        onNoteChange={(s, val) =>
                          setNoteBySlot((prev) => ({ ...prev, [slotKey(s)]: val }))
                        }
                        readOnly={false}
                        showOspiteName={showOspiteName}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-slate-800 mb-3">Pianificazione terapie extra</h4>
            {sortedTerapie.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna terapia extra configurata.</p>
            ) : (
              <ul className="space-y-3">
                {sortedTerapie.map((te) => (
                  <li
                    key={te.id}
                    className="rounded-lg border border-slate-200 p-3 bg-white"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Pill className="w-4 h-4 text-rose-500 shrink-0" />
                          <span className="font-semibold text-slate-900">{te.nome}</span>
                          <Badge variant={te.attiva ? "default" : "secondary"}>
                            {te.attiva ? "Attiva" : "Sospesa"}
                          </Badge>
                        </div>
                        {te.dosaggio ? (
                          <p className="text-sm text-slate-600 mt-1">{te.dosaggio}</p>
                        ) : null}
                        <p className="text-xs text-slate-500 mt-1">{summarizeTerapiaExtra(te)}</p>
                        {te.note ? (
                          <p className="text-xs text-slate-500 mt-1 italic">{te.note}</p>
                        ) : null}
                      </div>
                      {isAdmin ? (
                        <div className="flex flex-wrap gap-1.5 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingTerapia(te);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Modifica
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setStoricoTerapia(te)}
                          >
                            <History className="w-3.5 h-3.5 mr-1" />
                            Storico
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleAttiva(te)}
                          >
                            {te.attiva ? (
                              <>
                                <PauseCircle className="w-3.5 h-3.5 mr-1" />
                                Sospendi
                              </>
                            ) : (
                              <>
                                <PlayCircle className="w-3.5 h-3.5 mr-1" />
                                Riattiva
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(te)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Elimina
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {isAdmin ? (
        <>
          <TerapiaExtraDialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) setEditingTerapia(null);
            }}
            initialData={editingTerapia}
            onSubmit={handleCreateOrUpdate}
            saving={saving}
          />
          <TerapiaExtraStoricoDialog
            open={!!storicoTerapia}
            onOpenChange={(open) => {
              if (!open) setStoricoTerapia(null);
            }}
            fascicoloId={fascicoloId}
            terapia={storicoTerapia}
            authHeaders={authHeaders}
          />
        </>
      ) : null}
    </>
  );
}
